import { Table, TableBody } from "@mui/material";
import { Badge as IdentityIcon } from "@mui/icons-material";
import type {
  VendorProfileDocument,
  VendorProfileVendor,
} from "../types";
import { SectionCard } from "../components/SectionCard";
import { FieldRow } from "../components/FieldRow";
import { AttachmentDisplay } from "../components/AttachmentDisplay";
import { maskPan } from "../utils/masking";
import {
  formatCurrencyINR,
  formatDate,
  formatText,
} from "../utils/format";
import { findDocument } from "../utils/documentLookup";
import { MSME_DATE_LABEL } from "../utils/msme";

const VENDOR_TYPE_LABEL: Record<string, string> = {
  GST: "GST Registered",
  "Non-GST": "Non-GST",
  International: "International",
};

type Props = {
  vendor: VendorProfileVendor | null | undefined;
  primaryFacilityVolume?: number | null;
  primaryFacilityState?: string | null;
  primaryFacilityGst?: string | null;
  documents: VendorProfileDocument[] | undefined;
};

export function IdentityAndTaxSection({
  vendor,
  primaryFacilityVolume,
  primaryFacilityState,
  primaryFacilityGst,
  documents,
}: Props) {
  const panDoc = vendor
    ? findDocument(documents, "vendor", vendor.id, "PAN")
    : undefined;
  const msmeDoc = vendor
    ? findDocument(documents, "vendor", vendor.id, "MRC")
    : undefined;
  const tinDoc = vendor
    ? findDocument(
        documents,
        "vendor",
        vendor.id,
        "Tax_Identification_Number_Certificate",
      )
    : undefined;
  const rateCardDoc = vendor
    ? findDocument(documents, "vendor", vendor.id, "Master_Rate_Card")
    : undefined;
  const vendorType = vendor?.type ?? "GST";
  const isMsme = Boolean(vendor?.is_msme);
  const showRateCard =
    vendor?.nature_of_business === "Services" || Boolean(rateCardDoc);
  return (
    <SectionCard icon={<IdentityIcon fontSize="small" />} title="Identity & Tax">
      <Table size="small">
        <TableBody>
          {vendor && (
            <FieldRow label="Email ID" value={vendor.email_id} />
          )}
          <FieldRow
            label="Vendor Type"
            value={VENDOR_TYPE_LABEL[vendorType] ?? vendorType}
          />
          {vendor && (
            <FieldRow
              label="Nature of Business"
              value={formatText(vendor.nature_of_business)}
            />
          )}
          <FieldRow
            label="Type of Product / Service"
            value={formatText(vendor?.type_of_product_service)}
          />
          {vendorType !== "International" && (
            <FieldRow label="PAN Number" value={maskPan(vendor?.pan_card)} />
          )}
          {vendorType !== "International" && (
            <>
              <FieldRow
                label="Primary GSTIN"
                value={formatText(primaryFacilityGst)}
              />
              <FieldRow
                label="State of Registration"
                value={formatText(primaryFacilityState)}
              />
            </>
          )}
          <FieldRow
            label="Legal / Registered Name"
            value={formatText(vendor?.display_name)}
          />
          {vendor && (
            <>
              <FieldRow label="MSME Registered" value={isMsme ? "Yes" : "No"} />
              {isMsme && (
                <>
                  <FieldRow
                    label="MSME / Udyam Number"
                    value={formatText(vendor.msme_number)}
                  />
                  <FieldRow
                    label={MSME_DATE_LABEL}
                    value={formatDate(vendor.msme_registration_date)}
                  />
                  <FieldRow
                    label="MSME Certificate"
                    value={<AttachmentDisplay document={msmeDoc} />}
                  />
                </>
              )}
            </>
          )}
          {vendor && (
            <FieldRow
              label="TDS Category"
              value={formatText(vendor.tds_category)}
            />
          )}
          <FieldRow
            label="Expected Business Volume (INR)"
            value={formatCurrencyINR(primaryFacilityVolume)}
          />
          {vendorType !== "International" && (
            <FieldRow
              label="PAN Card"
              value={<AttachmentDisplay document={panDoc} />}
            />
          )}
          {showRateCard && (
            <FieldRow
              label="Master Rate Card"
              value={<AttachmentDisplay document={rateCardDoc} />}
            />
          )}
          {vendorType === "International" && (
            <>
              {vendor?.swift_bic && (
                <FieldRow label="SWIFT / BIC" value={vendor.swift_bic} />
              )}
              <FieldRow
                label="Tax Identification Number Certificate"
                value={<AttachmentDisplay document={tinDoc} />}
              />
            </>
          )}
        </TableBody>
      </Table>
    </SectionCard>
  );
}

export default IdentityAndTaxSection;
